// src/constants/detection.js
// Vocabulary for admin-authored detection rules. Kept in sync with the server
// validator in server/src/routes/detectionRules.js.

export const RULE_KINDS = ["condition", "ai_guidance"];
export const VERDICTS = ["Clean", "Spam", "Malicious"];
export const MATCH_MODES = [
  { value: "all", label: "ALL conditions (AND)" },
  { value: "any", label: "ANY condition (OR)" },
];
export const RULE_EFFECTS = [
  { value: "raise", label: "Raise verdict to" },
  { value: "suppress", label: "Suppress verdict to" },
];

// Each field: value, label, type. `type` decides which operators are offered.
export const FIELDS = [
  { value: "subject",          label: "Subject",                 type: "string" },
  { value: "body",             label: "Body text",               type: "string" },
  { value: "sender_email",     label: "Sender email",            type: "string" },
  { value: "sender_domain",    label: "Sender domain",           type: "string" },
  { value: "reply_to",         label: "Reply-To",                type: "string" },
  { value: "reply_to_domain",  label: "Reply-To domain",         type: "string" },
  { value: "url",              label: "Any URL",                 type: "string" },
  { value: "url_host",         label: "Any URL host",            type: "string" },
  { value: "attachment_name",  label: "Any attachment name",     type: "string" },
  { value: "attachment_mime",  label: "Any attachment MIME",     type: "string" },
  { value: "attachment_ext",   label: "Any attachment extension", type: "string" },
  { value: "spf_result",       label: "SPF result",              type: "string" },
  { value: "dkim_result",      label: "DKIM result",             type: "string" },
  { value: "dmarc_result",     label: "DMARC result",            type: "string" },
  { value: "header_risk",      label: "Header risk (low/medium/high)", type: "string" },
  { value: "social_level",     label: "Social-engineering level (low/medium/high)", type: "string" },
  { value: "social_score",     label: "Social-engineering score", type: "number" },
  { value: "url_count",        label: "URL count",               type: "number" },
  { value: "attachment_count", label: "Attachment count",        type: "number" },
  { value: "has_qr",           label: "Has QR code",             type: "boolean" },
  { value: "has_attachment",   label: "Has attachment",          type: "boolean" },
  { value: "has_deceptive_link", label: "Has deceptive link",    type: "boolean" },
];

export const FIELD_BY_VALUE = Object.fromEntries(FIELDS.map((f) => [f.value, f]));

export const OPERATORS = {
  string: [
    { value: "contains",     label: "contains" },
    { value: "not_contains", label: "does not contain" },
    { value: "equals",       label: "equals" },
    { value: "not_equals",   label: "does not equal" },
    { value: "starts_with",  label: "starts with" },
    { value: "ends_with",    label: "ends with" },
    { value: "regex",        label: "matches regex" },
    { value: "in_list",      label: "is in list (comma/newline separated)" },
  ],
  number: [
    { value: "gt",     label: ">" },
    { value: "lt",     label: "<" },
    { value: "gte",    label: "≥" },
    { value: "lte",    label: "≤" },
    { value: "equals", label: "=" },
  ],
  boolean: [
    { value: "is_true",  label: "is true" },
    { value: "is_false", label: "is false" },
  ],
};

export function fieldType(field) {
  return FIELD_BY_VALUE[field]?.type || "string";
}

export function operatorsFor(field) {
  return OPERATORS[fieldType(field)] || OPERATORS.string;
}

export function isBooleanField(field) {
  return fieldType(field) === "boolean";
}

const FIELD_LABEL = (v) => FIELD_BY_VALUE[v]?.label || v;
const OP_LABEL = (field, op) =>
  (OPERATORS[fieldType(field)] || []).find((o) => o.value === op)?.label || op;

/** Renders a condition rule as a readable sentence for list/finding display. */
export function describeRule(def) {
  if (!def || !Array.isArray(def.conditions)) return "";
  const joiner = def.match === "any" ? " OR " : " AND ";
  const parts = def.conditions.map((c) =>
    isBooleanField(c.field)
      ? `${FIELD_LABEL(c.field)} ${OP_LABEL(c.field, c.operator)}`
      : `${FIELD_LABEL(c.field)} ${OP_LABEL(c.field, c.operator)} "${c.value}"`
  );
  const effect = def.action?.effect === "suppress" ? "suppress verdict to" : "raise verdict to";
  return `If ${parts.join(joiner)} → ${effect} ${def.action?.verdict}`;
}
